import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import AssetSelector from "../../common/components/AssetSelector";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import { createTileLogo } from "../api/tileLogoApi";
import {
  getTileLogoNameError,
  validTileLogoFileSize,
  validTileLogoName,
} from "../validations/tileLogoValidations";
import styles from "./CreateTileLogoPage.module.css";

function CreateTileLogoPage() {
  const content = [
    "1. Only lowercase alphabetical characters allowed",
    "2. No special characters except '_'",
    "3. Use the most shortest and specific name as possible",
    "4. Avoid using multiple product names",
  ].join("\n");
  let history = useHistory();

  // Context state
  const { user } = useContext(UserContext);

  // page loading state
  const [isLoading, setIsLoading] = useState(false);

  // Input value states
  const [tileLogoName, setTileLogoName] = useState(null);
  const [tileLogoFile, setTileLogoFile] = useState(null);

  // Error states
  const [tileLogoNameError, setTileLogoNameError] = useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);

  function buildNotification(tileLogoId, name) {
    return {
      title: "Tile Logo added",
      content: `${name} has been added successfully`,
      ctaText: "View",
      tileLogoId: tileLogoId,
    };
  }

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    // Check if icon name passes validations
    if (validTileLogoName(tileLogoName)) {
      setTileLogoNameError(null);
    } else {
      setTileLogoNameError(getTileLogoNameError(tileLogoName));
    }

    //Check if icon file is selected
    if (tileLogoFile) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError("Tile Logo file cannot be empty");
    }

    if (validTileLogoName(tileLogoName) && tileLogoFile) {
      setIsLoading(true);

      // Check if icon file dimensions are the same as the icon size selected
      const isTileLogoFileSizeValid = await validTileLogoFileSize(tileLogoFile);

      if (isTileLogoFileSizeValid) {
        try {
          const tileLogoId = await createTileLogo(
            tileLogoName,
            tileLogoFile,
            user.uid
          );
          setIsLoading(false);
          history.push(`/${routes.TILE_LOGO}/${routes.ASSETS}`, {
            notification: buildNotification(tileLogoId, tileLogoName),
          });
        } catch (error) {
          setIsLoading(false);
          if (error.name === "FirebaseError") {
            console.log(error.message);
          } else if (
            error.message === "Tile Logo with the same name already exists"
          ) {
            setTileLogoNameError(error.message);
          } else {
            console.log(error);
          }
        }
      } else {
        setIsLoading(false);
        setAssetSelectorError(`Dimensions of Tile Logo file must be 40 x 40`);
      }
    }
  };

  return (
    <React.Fragment>
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <LoadingPage />
        </div>
      ) : null}
      <div className={styles.createPageContainer}>
        <div className={styles.breadcrumbContainer}>
          <Breadcrumb>
            <Crumb
              text="assets"
              tagProps={{
                to: `/${routes.TILE_LOGO}/${routes.ASSETS}`,
              }}
              as={Link}
            />
            <Separator />
            <Crumb
              text="add tile logo"
              tagProps={{
                to: `/${routes.TILE_LOGO}/${routes.ASSETS}/${routes.CREATE}`,
              }}
              as={Link}
            />
          </Breadcrumb>
        </div>
        <div>
          <h3 className={styles.heading}>Add Tile Logo</h3>
          <div className={styles.flagContainer}>
            <Flag
              page
              info
              title="How to name your tile logo"
              content={content}
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.tileLogoNameContainer}>
              <p className={styles.inputLabel}>Name</p>
              <div className={styles.inputContainer}>
                <TextField
                  required={!!tileLogoNameError}
                  size="m"
                  stretch
                  placeholder="Name"
                  type="text"
                  invalid={!!tileLogoNameError}
                  onChange={(e) => {
                    setTileLogoName(e.target.value);
                    setTileLogoNameError(null);
                  }}
                />
              </div>
              {tileLogoNameError ? (
                <Flag error title={tileLogoNameError} />
              ) : null}
            </div>
          </div>

          <div className={styles.assetSelectorContainer}>
            <div className={styles.inputContainer}>
              <AssetSelector
                heading="Tile Logo File"
                onAssetSelected={(file) => {
                  if (file) {
                    setTileLogoFile(file);
                    setAssetSelectorError(null);
                  }
                }}
              />
            </div>
            {assetSelectorError ? (
              <Flag error title={assetSelectorError} />
            ) : null}
          </div>
          <Stack>
            <Button primary onClick={handleSaveClick}>
              Save
            </Button>
            <Button secondary onClick={handeCancelClick}>
              Cancel
            </Button>
          </Stack>
        </div>
      </div>
    </React.Fragment>
  );
}

export default CreateTileLogoPage;
